import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function UserMenu() {
  const { user, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  if (!user) return null;

  const initials = user.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user.email[0].toUpperCase();

  const handleLogout = async () => {
    setOpen(false);
    await logout();
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-arena-surface transition-colors"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            className="w-7 h-7 rounded-full object-cover"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-arena-accent flex items-center justify-center text-xs font-bold text-white">
            {initials}
          </div>
        )}
        <span className="text-sm font-medium text-arena-text hidden sm:inline max-w-[120px] truncate">
          {user.name || user.email}
        </span>
        <svg
          className={`w-4 h-4 text-arena-muted transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-arena-surface border border-arena-border rounded-xl shadow-lg overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-arena-border">
            <div className="text-sm font-medium text-arena-text truncate">
              {user.name}
            </div>
            <div className="text-xs text-arena-muted truncate">{user.email}</div>
          </div>

          <div className="py-1">
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-arena-text hover:bg-arena-bg transition-colors"
            >
              Profile
            </Link>
            <Link
              to="/upload"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-arena-text hover:bg-arena-bg transition-colors"
            >
              Upload
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-arena-accent hover:bg-arena-bg transition-colors"
              >
                Admin
              </Link>
            )}
          </div>

          <div className="border-t border-arena-border py-1">
            <button
              onClick={handleLogout}
              className="block w-full text-left px-4 py-2 text-sm text-arena-muted hover:text-arena-text hover:bg-arena-bg transition-colors"
            >
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
